import {
  Box,
  Button,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ShieldIcon from "@mui/icons-material/Shield";
import type { IpEvent, SortField, SortOrder } from "@/types";
import { colors, tableHeadCellSx } from "@/theme/eventsTokens.ts";
import { PAGE_NEXT, PAGE_PREVIOUS } from "@/utils/vulnerabilityCopy.ts";
import EventRow from "./EventRow.tsx";

const headSx = {
  ...tableHeadCellSx,
  padding: { xs: "8px 6px", md: "10px 12px", lg: "12px 16px" },
} as const;

interface SortProps {
  readonly sortBy: SortField;
  readonly sortOrder: SortOrder;
  readonly onSort: (field: SortField) => void;
}

function SortableCell({ label, field, tooltip, sx: cellSx, sortBy, sortOrder, onSort }: SortProps & { label: string; field: SortField; tooltip?: string; sx?: object }) {
  const content = (
    <TableSortLabel data-testid={`sort-${field}`} active={sortBy === field} direction={sortBy === field ? sortOrder : "desc"} onClick={() => onSort(field)}>
      {tooltip ? <Tooltip title={tooltip}><span>{label}</span></Tooltip> : label}
    </TableSortLabel>
  );
  return <TableCell sx={{ ...headSx, ...cellSx }}>{content}</TableCell>;
}

function AuthGroupRow() {
  return (
    <TableRow sx={{ backgroundColor: colors.backgroundSecondary }}>
      <TableCell colSpan={7} sx={{ ...headSx, borderBottom: "none" }} />
      <TableCell colSpan={3} sx={{ ...headSx, textAlign: "center", borderBottom: "none", pb: 0, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em", color: colors.textMuted }}>
        Authentication
      </TableCell>
    </TableRow>
  );
}

function EventsTableHead({ sortBy, sortOrder, onSort }: SortProps) {
  return (
    <TableHead>
      <AuthGroupRow />
      <TableRow sx={{ backgroundColor: colors.backgroundSecondary }}>
        <TableCell sx={headSx}>IP Address</TableCell>
        <SortableCell label="Risk Score" field="fraud_score" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
        <SortableCell label="Country" field="country_code" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
        <TableCell sx={headSx}>ASN</TableCell>
        <SortableCell label="Last Activity" field="last_activity" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
        <TableCell sx={{ ...headSx, fontSize: "0.7rem", color: colors.textMuted }}>Activity</TableCell>
        <TableCell sx={{ ...headSx, textAlign: "center" }}>
          <Tooltip title="Reported by multiple sources">
            <ShieldIcon sx={{ fontSize: 16, color: colors.textMuted, verticalAlign: "middle" }} />
          </Tooltip>
        </TableCell>
        <SortableCell label="Blocks" field="blocked_recent" tooltip="Blocked authentication attempts in the recent activity window" sx={{ textAlign: "right" }} sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
        <SortableCell label="Failures" field="failures_recent" tooltip="Authentication failures in the recent activity window" sx={{ textAlign: "right" }} sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
        <TableCell sx={{ ...headSx, textAlign: "right", pr: { xs: "12px", md: "20px", lg: "24px" } }}>
          <Tooltip title="Successful authentications in the recent activity window"><span>Success</span></Tooltip>
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

const COL_WIDTHS = ["14%", "7%", "5%", "19%", "13%", "8%", "3%", "6%", "6%", "6%"];

export function EventsTableBody({
  items,
  isLoading,
  isFetching,
  error,
  sortBy,
  sortOrder,
  onSort,
}: {
  readonly items: IpEvent[];
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly error: Error | null;
  readonly sortBy: SortField;
  readonly sortOrder: SortOrder;
  readonly onSort: (field: SortField) => void;
}) {
  if (isLoading && items.length === 0) return <LoadingSkeleton />;
  if (error) return <ErrorState />;
  if (items.length === 0) return <EmptyState />;

  return (
    <Box
      aria-busy={isFetching}
      sx={{
        position: "relative",
        opacity: isFetching ? 0.5 : 1,
        transition: "opacity 0.3s",
        borderTop: `1px solid ${colors.border}`,
      }}
    >
      {isFetching && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
          }}
        />
      )}
      <TableContainer>
        <Table sx={{ tableLayout: "fixed" }}>
          <colgroup>
            {COL_WIDTHS.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>
          <EventsTableHead sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
          <TableBody>
            {items.map((event, i) => (
              <EventRow key={`${event.ip_address}-${i}`} event={event} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function LoadingSkeleton() {
  return (
    <Box sx={{ p: 3 }}>
      {Array.from({ length: 12 }, (_, i) => (
        <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 1 }} />
      ))}
    </Box>
  );
}

function ErrorState() {
  return (
    <Alert severity="error" sx={{ m: 2 }}>
      Failed to load events. Please try again later.
    </Alert>
  );
}

function EmptyState() {
  return (
    <Box sx={{ p: 6, textAlign: "center" }}>
      <Typography sx={{ color: colors.textSecondary, mb: 1 }}>
        No events found matching the current filters
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: colors.textMuted }}>
        Try adjusting your filter criteria
      </Typography>
    </Box>
  );
}

interface ToolbarProps {
  readonly page: number;
  readonly pageCount: number;
  readonly total: number;
  readonly showFrom: number;
  readonly showTo: number;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onExport: () => void;
  readonly lastUpdated?: string;
  readonly isFetching: boolean;
}

export function EventsToolbar({
  page, pageCount, total, showFrom, showTo, onPrev, onNext, onExport, lastUpdated, isFetching,
}: ToolbarProps) {
  return (
    <Box
      data-testid="events-toolbar"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <ToolbarLabel showFrom={showFrom} showTo={showTo} total={total} lastUpdated={lastUpdated} />
      <ToolbarActions
        page={page}
        pageCount={pageCount}
        total={total}
        onPrev={onPrev}
        onNext={onNext}
        onExport={onExport}
        isFetching={isFetching}
      />
    </Box>
  );
}

function formatLastUpdated(raw: string): string {
  const epoch = Number(raw);
  const date = Number.isFinite(epoch) && epoch > 0 ? new Date(epoch * 1000) : new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function ToolbarLabel({ showFrom, showTo, total, lastUpdated }: { readonly showFrom: number; readonly showTo: number; readonly total: number; readonly lastUpdated?: string }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: colors.textPrimary }}>
        IP Activity
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: colors.textSecondary }}>
        Showing {showFrom}–{showTo} of {total.toLocaleString()} addresses
        {lastUpdated && ` · Last updated: ${formatLastUpdated(lastUpdated)}`}
      </Typography>
    </Box>
  );
}

function ToolbarActions({
  page, pageCount, total, onPrev, onNext, onExport, isFetching,
}: {
  readonly page: number;
  readonly pageCount: number;
  readonly total: number;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onExport: () => void;
  readonly isFetching: boolean;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Tooltip title="Export up to 1 000 IPs as CSV">
        <span>
          <Button
            variant="outlined"
            size="small"
            onClick={onExport}
            disabled={total === 0 || isFetching}
            startIcon={<FileDownloadIcon sx={{ fontSize: "16px !important" }} />}
            sx={{
              textTransform: "none",
              fontSize: "0.8125rem",
              borderColor: colors.border,
              color: colors.textSecondary,
              mr: 1,
              "&:hover": { borderColor: colors.textSecondary, backgroundColor: colors.backgroundMuted },
            }}
          >
            Export CSV
          </Button>
        </span>
      </Tooltip>
      <PageNav page={page} pageCount={pageCount} onPrev={onPrev} onNext={onNext} isFetching={isFetching} />
    </Box>
  );
}

export function PageNav({
  page, pageCount, onPrev, onNext, isFetching,
}: {
  readonly page: number;
  readonly pageCount: number;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly isFetching: boolean;
}) {
  const btnSx = { minWidth: "auto", p: 0.75, borderColor: colors.border, color: colors.textSecondary };
  return (
    <>
      <Button variant="outlined" size="small" disabled={page === 0 || isFetching} onClick={onPrev} sx={btnSx} data-testid="page-prev" aria-label={PAGE_PREVIOUS}>
        <ChevronLeftIcon sx={{ fontSize: 18 }} />
      </Button>
      <Typography data-testid="page-info" sx={{ fontSize: "0.875rem", color: colors.textMuted, px: 1 }}>
        Page {page + 1} of {pageCount}
      </Typography>
      <Button variant="outlined" size="small" disabled={page >= pageCount - 1 || isFetching} onClick={onNext} sx={btnSx} data-testid="page-next" aria-label={PAGE_NEXT}>
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </Button>
    </>
  );
}
